import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

// Edit an existing raw-material classification. Shared across Diamonds /
// Colour Stones / Pearls so the "Tag Display Name" field behaves identically
// everywhere. The schema code is immutable (it prefixes packet numbers) so it
// is shown read-only.
const MATERIAL_CONFIG = {
  diamond: { endpoint: '/diamonds/classifications', title: 'Edit Diamond Classification' },
  color_stone: { endpoint: '/color-stones/classifications', title: 'Edit Colour Stone Classification' },
  pearl: { endpoint: '/pearls/classifications', title: 'Edit Pearl Classification' },
};

export function ClassificationEditDialog({ open, onOpenChange, companyId, material = 'diamond', classification, onUpdated }) {
  const cfg = MATERIAL_CONFIG[material] || MATERIAL_CONFIG.diamond;
  const [form, setForm] = useState({ shape_name: '', description: '', tag_display_name: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && classification) {
      setForm({
        shape_name: classification.shape_name || '',
        description: classification.description || '',
        tag_display_name: classification.tag_display_name || '',
      });
    }
  }, [open, classification]);

  const submit = async () => {
    if (!classification) return;
    if (!form.shape_name.trim()) { toast.error('Shape name is required'); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`${cfg.endpoint}/${classification.id}`, {
        shape_name: form.shape_name,
        description: form.description,
        tag_display_name: form.tag_display_name,
        company_id: companyId,
      });
      toast.success('Classification updated');
      onUpdated && onUpdated(data);
      onOpenChange(false);
    } catch (e) { toast.error(extractError(e, 'Classification update failed')); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="classification-edit-dialog">
        <DialogHeader>
          <DialogTitle>{cfg.title}</DialogTitle>
          <DialogDescription>
            Update the shape name, tag display name, or description. The schema code cannot be changed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Schema code</Label>
            <Input value={classification?.schema_code || ''} disabled className="font-mono" data-testid="classification-edit-schema" />
          </div>
          <div>
            <Label>Shape name</Label>
            <Input value={form.shape_name} onChange={(e) => setForm({ ...form, shape_name: e.target.value })}
              placeholder="e.g. Round Brilliant" data-testid="classification-edit-shape-input" />
          </div>
          <div>
            <Label>Tag Display Name (optional)</Label>
            <Input value={form.tag_display_name} onChange={(e) => setForm({ ...form, tag_display_name: e.target.value })}
              placeholder="Printed on jewellery tags instead of the code" data-testid="classification-edit-tagname-input" />
            <p className="text-[11px] text-slate-500 mt-1">
              Printed on jewellery tags exactly as typed. Leave blank to print the classification name.
            </p>
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
              data-testid="classification-edit-description-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} className="luxury-button" disabled={saving} data-testid="classification-edit-submit-button">
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ClassificationEditDialog;
